import type { SortDirection } from "@/types/table-column";

/**
 * Interfaces for filter types
 */
export interface NumberFilter {
  min?: string | number;
  max?: string | number;
}

export interface DateFilter {
  from?: string;
  to?: string;
}

export type FilterValue = string | string[] | NumberFilter | DateFilter | null;

export type NullSort = "first" | "last" | "min" | "max";
export interface QueryParams {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_direction?: SortDirection;
  search?: string;
  null_sort?: NullSort;
  metrics?: string[];
  status?: string[];
  filters?: string;
  metric_filters?: string | Record<string, unknown>;
  extra_fields?: string;
  formulas?: Array<Record<string, unknown>>;
  rule_set_id?: string; // Custom channels ruleset selection
  [key: string]:
    | string
    | number
    | boolean
    | string[]
    | FilterValue
    | Record<string, unknown>
    | Array<Record<string, unknown>>
    | undefined;
}

/**
 * Processes query parameters for API requests
 * @param params Raw query parameters
 * @returns URLSearchParams object with processed parameters
 */
export function processQueryParams(params: QueryParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  // Process basic parameters
  if (params.limit) searchParams.set("limit", params.limit.toString());
  if (params.offset !== undefined)
    searchParams.set("offset", params.offset.toString());
  if (params.sort_by) searchParams.set("sort_by", params.sort_by);
  if (params.sort_direction)
    searchParams.set("sort_direction", params.sort_direction);
  if (params.search) searchParams.set("search", params.search);
  if (params.null_sort) searchParams.set("null_sort", params.null_sort);

  // Explicitly handle extra_fields before the generic loop
  if (
    params.extra_fields &&
    typeof params.extra_fields === "string" &&
    params.extra_fields.trim() !== ""
  ) {
    searchParams.set("extra_fields", params.extra_fields);
  }

  // Handle rule_set_id (custom channels ruleset selection)
  if (
    params.rule_set_id &&
    typeof params.rule_set_id === "string" &&
    params.rule_set_id.trim() !== ""
  ) {
    searchParams.set("rule_set_id", params.rule_set_id);
  }

  // List of parameters to handle in a special way (not as regular filters)
  const specialParams = [
    "limit",
    "offset",
    "sort_by",
    "sort_direction",
    "search",
    "null_sort",
    "metrics", // Metrics param - handled as array of strings
    "status", // Status param - handled as array of strings
    "filters", // Filters param - handled as JSON
    "metric_filters", // Metric filters param - handled as JSON
    "relation_filters", // Relation filters param - handled as JSON
    "formulas", // Formulas param - handled as JSON
    "extra_fields", // Add extra_fields to prevent it from being treated as a generic filter
    "rule_set_id", // Custom channels ruleset selection - handled as simple string
  ];

  // Process special array parameters
  if (
    params.metrics &&
    Array.isArray(params.metrics) &&
    params.metrics.length > 0
  ) {
    params.metrics.forEach((metric) => {
      searchParams.append("metrics", String(metric));
    });
  }

  if (
    params.status &&
    Array.isArray(params.status) &&
    params.status.length > 0
  ) {
    params.status.forEach((status) => {
      searchParams.append("status", String(status));
    });
  }

  // Handle filters (string or object)
  if (params.filters) {
    if (typeof params.filters === "string" && params.filters.trim() !== "") {
      searchParams.set("filters", params.filters);
    } else if (
      typeof params.filters === "object" &&
      Object.keys(params.filters).length > 0
    ) {
      searchParams.set("filters", JSON.stringify(params.filters));
    }
  }

  // Handle metric_filters (string or object)
  if (params.metric_filters) {
    if (
      typeof params.metric_filters === "string" &&
      params.metric_filters.trim() !== ""
    ) {
      searchParams.set("metric_filters", params.metric_filters);
    } else if (
      typeof params.metric_filters === "object" &&
      Object.keys(params.metric_filters).length > 0
    ) {
      searchParams.set("metric_filters", JSON.stringify(params.metric_filters));
    }
  }

  // Handle relation_filters (string - already JSON stringified)
  if (params.relation_filters) {
    if (
      typeof params.relation_filters === "string" &&
      params.relation_filters.trim() !== ""
    ) {
      searchParams.set("relation_filters", params.relation_filters);
    }
  }

  // Handle formulas (array of objects)
  if (
    params.formulas &&
    Array.isArray(params.formulas) &&
    params.formulas.length > 0
  ) {
    searchParams.set("formulas", JSON.stringify(params.formulas));
  }

  // Process filter parameters
  Object.entries(params).forEach(([key, value]) => {
    // Skip the special parameters we've already processed
    if (specialParams.includes(key)) {
      return;
    }

    if (value !== undefined && value !== null && value !== "") {
      if (Array.isArray(value)) {
        // For array values (multi-select filters)
        if (value.length > 0) {
          // If the key doesn't already end with '_filter', append it
          const paramKey = key.endsWith("_filter") ? key : `${key}_filter`;
          value.forEach((val) => {
            if (val !== undefined && val !== null && val !== "") {
              searchParams.append(paramKey, String(val));
            }
          });
        }
      } else if (typeof value === "object" && value !== null) {
        // Handle range filters (NumberFilter)
        const numFilter = value as NumberFilter;
        if (
          "min" in numFilter &&
          numFilter.min !== undefined &&
          numFilter.min !== ""
        ) {
          searchParams.set(`${key}_min`, String(numFilter.min));
        }
        if (
          "max" in numFilter &&
          numFilter.max !== undefined &&
          numFilter.max !== ""
        ) {
          searchParams.set(`${key}_max`, String(numFilter.max));
        }

        // Handle date filters (DateFilter)
        const dateFilter = value as DateFilter;
        if (
          "from" in dateFilter &&
          dateFilter.from !== undefined &&
          dateFilter.from !== ""
        ) {
          searchParams.set(`${key}_from`, dateFilter.from);
        }
        if (
          "to" in dateFilter &&
          dateFilter.to !== undefined &&
          dateFilter.to !== ""
        ) {
          searchParams.set(`${key}_to`, dateFilter.to);
        }
      } else {
        // Handle simple filters
        const paramKey = key.endsWith("_filter") ? key : `${key}_filter`;
        searchParams.set(paramKey, String(value));
      }
    }
  });

  return searchParams;
}
